import { computed } from 'mobx';
import * as moment from 'moment';
import * as React from 'react';
import { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ICONS } from '../../../assets/Icons';
import { graphStyle } from '../../../assets/styles/Layout/AreaGraph';
import { CONSTANTS } from '../../../assets/UIConstants';
import { Scan } from '../../../data_layer/models/Scan';
import { ScanStore } from '../../../data_layer/stores/ScanStore';
import { Comparision } from '../../pages/Public';
import { CurrentValue } from '../CurrentValue';
import { AreaGraph, NumericRange } from '../Graphs/AreaGraph';
import { Panel } from '../Panel';
import { PanelFooter } from '../PanelFooter';
import { ValueDelta } from '../ValueDelta';

export interface AbstractPanelProps {
    scanStore: ScanStore;
    scan_id?: number;
    sex?: string;
}

export interface IDataPoint {
    x: number;
    y: number;
}

const currentValueContainerStyle: React.CSSProperties = {
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
};

const linkContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginRight: 25,
    fontSize: 18,
    height: 90,
};
const defaultLinkStyle: React.CSSProperties = {
    textDecoration: 'none',
    color: CONSTANTS.COLORS.THEME_BLUE,
};

export abstract class AbstractPanel extends React.Component<AbstractPanelProps> {
    protected abstract panelId: string;
    protected abstract panelTitle: string;
    protected abstract popoverTitle: string;
    protected abstract popoverBody: string | JSX.Element;
    protected abstract fullSummaryLinkPath: string;
    protected goodValueComparisionValue: number | undefined;
    protected goodValueComparision: Comparision | undefined;

    protected abstract graphUnit: string;
    protected graphColor: string = CONSTANTS.COLORS.THEME_BLUE;

    protected currentScan: Scan = this.props.scanStore.mostRecentScan;

    public constructor(props: AbstractPanelProps) {
        super(props);
        if (props.scan_id != undefined) {
            this.currentScan = this.props.scanStore.getSingleRecord(props.scan_id);
        }
    }

    protected additionalFooter(): React.ReactNode {
        return null;
    }

    protected generateXLabel(value: number): string {
        const date = moment(value);
        return date.format('M/DD');
    }

    protected abstract yValueExtractor(scan: Scan): number;

    @computed protected get graphDataPoints(): IDataPoint[] {
        const dataPoints: IDataPoint[] = [];
        this.props.scanStore.recentScans.forEach((scan: Scan) => {
            const tmp: IDataPoint = {
                x: scan.created_at.valueOf(),
                y: this.yValueExtractor(scan),
            };
            dataPoints.push(tmp);
        });
        return dataPoints;
    }

    @computed protected get currentValue(): number {
        return this.yValueExtractor(this.currentScan);
    }

    @computed protected get deltaValue(): number {
        if (this.props.scanStore.recentScans.length > 1) {
            const previousPoint = this.props.scanStore.recentScans[this.props.scanStore.recentScans.length - 2];
            return this.currentValue - this.yValueExtractor(previousPoint);
        }
        return 0;
    }

    @computed protected get yAxisRange(): NumericRange | undefined {
        if (this.graphDataPoints.length >= 1) {
            let maxValue: number = this.graphDataPoints[0].y;
            this.graphDataPoints.forEach(entry => {
                if (entry.y > maxValue) {
                    maxValue = entry.y;
                }
            });
            return {
                min: 0,
                max: Math.ceil(maxValue * 1.15),
            };
            // sets maximum value on y axis to 15% more than the highest value in the measurements.
            // this has no functional implications, it just ensures a consistent visual scale for our graphs.
        }
        return undefined;
    }

    protected renderFullSummaryLink(containerStyle?: CSSProperties, linkStyle?: CSSProperties): JSX.Element {
        let route = `/full-summary/${this.fullSummaryLinkPath}`;
        if (undefined != this.props.scan_id) {
            route = `/history/${this.props.scan_id}/full-summary/${this.fullSummaryLinkPath}`;
        }
        return (
            <div style={containerStyle || linkContainerStyle}>
                <Link style={linkStyle || defaultLinkStyle} to={route}>
                    See Full Summary
                </Link>
            </div>
        );
    }

    public render(): JSX.Element {
        return (
            <Panel
                id={this.panelId}
                width={500}
                height={410}
                title={this.panelTitle}
                icon={ICONS.QUESTION_MARK}
                popoverTitle={this.popoverTitle}
                popoverBody={this.popoverBody}
            >
                <div style={graphStyle.container}>
                    <AreaGraph
                        width={500}
                        height={250}
                        dataPoints={this.graphDataPoints}
                        accentColor={this.graphColor}
                        hasGrid={true}
                        xAxisTickFormatter={this.generateXLabel}
                        yAxisRange={this.yAxisRange}
                        goodValueComparision={this.goodValueComparision}
                        goodValueComparisionValue={this.goodValueComparisionValue}
                    />
                    <PanelFooter>
                        <div style={currentValueContainerStyle}>
                            <CurrentValue
                                value={this.currentValue}
                                unit={this.graphUnit}
                                valueFontSize={32}
                                unitFontSize={16}
                            />
                            <ValueDelta marginLeft={10} value={this.deltaValue} />
                            {this.additionalFooter()}
                        </div>
                        {this.renderFullSummaryLink()}
                    </PanelFooter>
                </div>
            </Panel>
        );
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/components/dashboardPanels/AbstractPanel.tsx